<template>
    <div class="col-md-12">
        <div class="h2 mb-2">Planning</div>
        <div class="flash-message">
            <FlashMessage :position="'top'"></FlashMessage>
        </div>
        <div class="h4">
            <span>Auto planning</span>
            <span class="material-icons-outlined cursor-pointer ml-1"
                  title="Enable/disable cron jobs for automatic scheduling and set it to a specific time every day."
            >help</span>
        </div>
        <div class="row">
            <div class="col-md-12">
                <div class="form-group">
                    <div class="custom-control custom-checkbox mr-2">
                        <input type="checkbox" class="custom-control-input" id="autoPlan"
                               v-model="autoplanSettings.isEnabled">
                        <label class="custom-control-label" for="autoPlan">
                            <b>Enabled</b>
                        </label>
                    </div>
                    <div class="d-flex align-items-center bg-white border rounded-lg div-focus border-2" style="max-width: 200px">
                        <vue-timepicker
                            manual-input
                            placeholder="Time"
                            v-model="autoplanSettings.cronTime"
                            input-class="border-0 py-0 shadow-none form-input">
                        </vue-timepicker>
                        <span
                            class="material-icons-outlined pr-2 text-primary">schedule</span>
                        <div class="help-block with-errors"></div>
                    </div>
                </div>
            </div>
        </div>
        <div class="row mt-2">
            <div class="col-md-3">
                <div class="form-group">
                    <label for="date" class="d-block mb-0">
                        <strong>Date</strong>
                        <span class="material-icons-outlined cursor-pointer ml-1" title="Date to be planned.">help</span>
                    </label>
                    <div class="align-items-center bg-white border rounded-lg div-focus border-2">
                        <v-select
                            :options="dateOptions"
                            v-model="autoplanSettings.date"
                            id="date"
                        >
                        </v-select>
                    </div>
                </div>
                <div class="form-group">
                    <label for="startWith" class="d-block mb-0">
                        <strong>Start with</strong>
                        <span class="material-icons-outlined cursor-pointer ml-1"
                              title="Start planning from existing routes or from scratch.
EMPTY – Ignore existing routes and start from scratch
CURRENT – Start planning with the existing routes"
                        >help</span>
                    </label>
                    <div class="align-items-center bg-white border rounded-lg div-focus border-2">
                        <v-select
                            :options="startWithOptions"
                            v-model="autoplanSettings.startWith"
                            id="startWith"
                        >
                        </v-select>
                    </div>
                </div>
                <div class="form-group">
                    <label for="clustering" class="d-block mb-0">
                        <strong>Clustering</strong>
                        <span class="material-icons-outlined cursor-pointer ml-1"
                              title="If true the system will try to minimize overlapp between driver routes."
                        >help</span>
                    </label>
                    <div class="align-items-center bg-white border rounded-lg div-focus border-2">
                        <v-select
                            :options="[true, false]"
                            v-model="autoplanSettings.clustering"
                            id="clustering"
                        >
                        </v-select>
                    </div>
                </div>
                <div class="form-group">
                    <label for="includeScheduledOrders" class="d-block mb-0">
                        <strong>Include scheduled orders</strong>
                        <span class="material-icons-outlined cursor-pointer ml-1"
                              title="Determines behavior when a subset of orders is selected but there are other orders already scheduled to the applicable drivers.
If includeScheduledOrders is true, these orders will be included in the optimization, otherwise they will be unscheduled."
                        >help</span>
                    </label>
                    <div class="align-items-center bg-white border rounded-lg div-focus border-2">
                        <v-select
                            :options="[true, false]"
                            v-model="autoplanSettings.includeScheduledOrders"
                            id="includeScheduledOrders"
                        >
                        </v-select>
                    </div>
                </div>
            </div>
            <div class="col-md-3">
                <div class="form-group">
                    <label for="balancing" class="d-block mb-0">
                        <strong>Balancing</strong>
                        <span class="material-icons-outlined cursor-pointer ml-1"
                              title="Route balancing settings.
OFF – No balancing
ON – Balance routes
ON_FORCE – Balance routes and use all drivers"
                        >help</span>
                    </label>
                    <div class="align-items-center bg-white border rounded-lg div-focus border-2">
                        <v-select
                            :options="balancingOptions"
                            v-model="autoplanSettings.balancing"
                            id="balancing"
                        >
                        </v-select>
                    </div>
                </div>

                <div class="form-group">
                    <label for="lockType" class="d-block mb-0">
                        <strong>Lock Type</strong>
                        <span class="material-icons-outlined cursor-pointer ml-1"
                              title="Applicable if startWith is set to CURRENT.
    NONE – Allow all changes to the existing routes
    ROUTES - Keep planned Routes unchanged and add new Orders to unused
    RESOURCES - Keep Drivers/Vehicles for planned Orders and fit in new Orders"
                        >help</span>
                    </label>
                    <div class="align-items-center bg-white border rounded-lg div-focus border-2">
                        <v-select
                            :options="lockTypeOptions"
                            v-model="autoplanSettings.lockType"
                            id="lockType"
                        >
                        </v-select>
                    </div>
                </div>
                <div class="form-group">
                    <label for="useDrivers" class="d-block mb-0">
                        <strong>Use Drivers</strong>
                        <span class="material-icons-outlined cursor-pointer ml-1"
                              title="Selected drivers that should be used in the optimization.
    By default (if useDrivers is not set or the list is empty) all available drivers for the specified date are used."
                        >help</span>
                    </label>
                    <div class="align-items-center bg-white border rounded-lg div-focus border-2">
                        <v-select
                            :options="driverList"
                            v-model="autoplanSettings.useDrivers"
                            id="useDrivers"
                            multiple="multiple"
                        >
                        </v-select>
                    </div>
                </div>
            </div>
            <div class="col-md-3">
                <div class="form-group">
                    <label for="balanceBy" class="d-block mb-0">
                        <strong>Balance By</strong>
                        <span class="material-icons-outlined cursor-pointer ml-1"
                              title="If route balancing is turned on, this defines the criteria for balancing the routes.
WT – Working time
NUM – Number of orders per driver"
                        >help</span>
                    </label>
                    <div class="align-items-center bg-white border rounded-lg div-focus border-2">
                        <v-select
                            :options="balanceByOptions"
                            v-model="autoplanSettings.balanceBy"
                            id="balanceBy"
                        >
                        </v-select>
                    </div>
                </div>
                <div class="form-group">
                    <label for="depotTrips" class="d-block mb-0">
                        <strong>Depot Trips</strong>
                        <span class="material-icons-outlined cursor-pointer ml-1"
                              title="If true drivers will be scheduled to return to the warehouse to reload (or unload in case of pickups) if there is still time within their working hours to do another route.
More information is available on the blog:
https://optimoroute.com/stop-restock-and-continue-on-your-way/"
                        >help</span>
                    </label>
                    <div class="align-items-center bg-white border rounded-lg div-focus border-2">
                        <v-select
                            :options="[true, false]"
                            v-model="autoplanSettings.depotTrips"
                            id="depotTrips"
                        >
                        </v-select>
                    </div>
                </div>
                <div class="form-group">
                    <label for="useOrders" class="d-block mb-0">
                        <strong>Use Orders</strong>
                        <span class="material-icons-outlined cursor-pointer ml-1"
                              title="Deprecated in favor of useOrderObjects.
Selected orders that should be included in the optimization.
Orders are specified as a list of order identifiers (orderNo strings).
By default (if useOrders is not set or the list is empty) all orders for the specified date that have a valid location are included."
                        >help</span>
                    </label>
                    <div class="align-items-center bg-white border rounded-lg div-focus border-2">
                        <v-select
                            :options="[]"
                            v-model="autoplanSettings.useOrders"
                            id="useOrders"
                        >
                        </v-select>
                    </div>
                </div>
            </div>
            <div class="col-md-3">
                <div class="form-group">
                    <label for="balancingFactor" class="d-block mb-0">
                        <strong>Balancing Factor</strong>
                        <span class="material-icons-outlined cursor-pointer ml-1"
                              title="Importance of balancing compared to route costs.
Increasing the balancing factor will result in more balanced routes.
Only applicable in combination with ON_FORCE (otherwise ignored).

Minimum value: 0.0
Maximum value: 1.0"
                        >help</span>
                    </label>
                    <div class="align-items-center bg-white border rounded-lg div-focus border-2">
                        <input
                            class="form-control border-0 py-0 shadow-none form-input"
                            type="text"
                            id="balancingFactor"
                            v-model="autoplanSettings.balancingFactor"
                            @change="onChangeBalancingFactor"
                        />
                    </div>
                </div>
                <div class="form-group">
                    <label for="depotVisitDuration" class="d-block mb-0">
                        <strong>Depot visit duration</strong>
                        <span class="material-icons-outlined cursor-pointer ml-1"
                              title="Time in minutes required to reload or unload the goods in the warehouse before the driver goes out for another run.
Only applicable if depotTrips is set to true."
                        >help</span>
                    </label>
                    <div class="align-items-center bg-white border rounded-lg div-focus border-2">
                        <input
                            class="form-control border-0 py-0 shadow-none form-input"
                            type="number"
                            min="0"
                            id="depotVisitDuration"
                            v-model="autoplanSettings.depotVisitDuration"
                        />
                    </div>
                </div>
                <div class="form-group">
                    <label for="useOrderObjects" class="d-block mb-0">
                        <strong>Use Order Objects</strong>
                        <span class="material-icons-outlined cursor-pointer ml-1"
                              title="Subset of orders on the planned date that should be included in the optimization."
                        >help</span>
                    </label>
                    <div class="align-items-center bg-white border rounded-lg div-focus border-2">
                        <v-select
                            :options="[]"
                            v-model="autoplanSettings.useOrderObjects"
                            id="useOrderObjects"
                        >
                        </v-select>
                    </div>
                </div>
            </div>
        </div>
        <div class="row">
            <div class="col-12">
                <button type="button" class="btn btn-success btn-lg mr-3" @click="save">
                    Save
                </button>
                <button type="button" class="btn btn-success btn-lg" @click="sendManually">
                    Plan manually
                </button>
            </div>
        </div>
    </div>
</template>
<script>
import 'vue-select/dist/vue-select.css';
import VueTimepicker from 'vue2-timepicker';
import 'vue2-timepicker/dist/VueTimepicker.css';

export default {
    name: "RoutesPlanning",
    props: {
        dateOptions: {type: Array, required: true},
        autoplanSettingsProp: {type: Object, required: true},
        balancingOptions: {type: Array, required: true},
        balanceByOptions: {type: Array, required: true},
        startWithOptions: {type: Array, required: true},
        lockTypeOptions: {type: Array, required: true},
        driverList: {type: Array, required: true},
    },
    components: {
        VueTimepicker
    },
    data() {
        return {
            autoplanSettings: JSON.parse(JSON.stringify(this.autoplanSettingsProp))
        }
    },
    methods: {
        onChangeBalancingFactor() {
            let balancingFactor = this.autoplanSettings.balancingFactor;
            balancingFactor = balancingFactor.replaceAll(/,/gi, '.');
            balancingFactor = balancingFactor.replaceAll(/[^.0-9-]+/gi, '');

            balancingFactor = parseFloat(balancingFactor);

            if (balancingFactor > 1) {
                balancingFactor = 1;
            } else if (balancingFactor < 0) {
                balancingFactor = 0;
            }

            this.autoplanSettings.balancingFactor = balancingFactor;
        },
        sendManually() {
            this.sendRequest(`/start-routes-planning`, this.autoplanSettings);
        },
        save() {
            this.sendRequest(`/routes-planning`, this.autoplanSettings);
        },
        sendRequest(url, params) {
            axios.post(url, params).then((response) => {
                    this.flashMessage.success({
                        title: '',
                        message: response.data.message,
                        time: 1000,
                        blockClass: 'flash-message'
                    });
                })
                .catch(err => {
                    this.flashError(err);
                })
        },
        flashError(error, defaultMassage = 'Error') {
            console.error(error.response);
            this.flashMessage.error({
                title: '',
                message: error.response.data.message || error.response.data ||  defaultMassage,
                time: 4000,
                blockClass: 'flash-message error'
            });
        },

    },
}
</script>
<style scoped>
.material-icons-outlined {
    font-size: 0.9rem;
}
.form-input {
    height: 35px;
}


</style>
